import { useEffect, useState, type ReactNode } from "react";
import { api } from "../../api/apiClient";
import type { AccountLinkPreview, AccountLinkPurpose, AppLanguage } from "../../api/types";
import { DEFAULT_LANGUAGE, fromAppLanguage, intlLocale } from "../../i18n/languages";
import { activateLanguage, deactivateLanguage, loadLanguage } from "../../i18n/store";

/**
 * G-136 — the secret from an emailed link. It travels in the URL FRAGMENT
 * (`#token=…`), which the browser never sends to any server: it is in no
 * access log and in no Referer. Read once, then wiped from the address bar so
 * it does not linger in history or get copied along with the URL.
 */
export function useLinkToken(): string {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "");
  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(window.history.state, "", window.location.pathname);
    }
  }, []);
  return token;
}

export type LinkState =
  | { kind: "loading" }
  | { kind: "unusable" }
  | { kind: "ready"; preview: AccountLinkPreview };

/**
 * What the link is for — asked of the server without using it up, so opening
 * the page (or a mail scanner opening it) changes nothing. Anything that is
 * not usable reads the same, whichever of the reasons it is.
 */
export function useLinkPreview(token: string, accept: AccountLinkPurpose[]): LinkState {
  const [state, setState] = useState<LinkState>({ kind: "loading" });
  const acceptKey = accept.join(",");
  useEffect(() => {
    if (!token) {
      setState({ kind: "unusable" });
      return;
    }
    let alive = true;
    api
      .previewAccountLink(token)
      .then((preview) => {
        if (!alive) return;
        setState(acceptKey.split(",").includes(preview.purpose) ? { kind: "ready", preview } : { kind: "unusable" });
      })
      .catch(() => {
        if (alive) setState({ kind: "unusable" });
      });
    return () => {
      alive = false;
    };
  }, [token, acceptKey]);
  return state;
}

/**
 * The page speaks the language the email did — the person's own (OD-196).
 * The CRM is otherwise English, so on leaving the page the store goes back
 * to English, the way the kiosk's provider does on unmount.
 */
export function useAccountLanguage(language: AppLanguage | null | undefined): void {
  const code = fromAppLanguage(language) ?? DEFAULT_LANGUAGE;
  useEffect(() => {
    activateLanguage(code);
    document.documentElement.setAttribute("lang", intlLocale(code));
    void loadLanguage(code).catch(() => undefined);
    return () => {
      deactivateLanguage();
      document.documentElement.setAttribute("lang", intlLocale(DEFAULT_LANGUAGE));
    };
  }, [code]);
}

/** The sign-in card's frame, for the account pages nobody is signed in on. */
export function AccountShell({ children }: { children: ReactNode }) {
  return (
    <div className="login-shell">
      <div className="login-card">
        <div className="login-brand">
          <img className="login-brand-logo login-brand-logo-light" src="/fw-stacked-rose.svg" alt="" aria-hidden="true" />
          <img className="login-brand-logo login-brand-logo-dark" src="/fw-stacked-ondark.svg" alt="" aria-hidden="true" />
        </div>
        {children}
      </div>
    </div>
  );
}

/** HTTP status of an API error, if it carried one. */
export function errorStatus(err: unknown): number | null {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : null;
}
